import { ReactNode, useEffect } from "react";
import { PostHogProvider, usePostHog } from "@posthog/react";
import type { PostHogConfig } from "posthog-js";

import { toPhone } from "common/utils/formatting";
import { Optional } from "common/utils/types";

const postHogOptions: Partial<PostHogConfig> = {
  autocapture: false,
  capture_pageview: false,
  disable_session_recording: true,
};

/** Base properties supplied by ClientBundle._get_initial_user_data */
export type PostHogAnalyticsUser = {
  id: number;
  email: Optional<string>;
  phone_number: Optional<number>;
  first_name: Optional<string>;
  last_name: Optional<string>;
};

export type PostHogAnalyticsIdentity = {
  uniqueId: string;
  email: Optional<string>;
  phoneNumber: Optional<number>;
  firstName: Optional<string>;
  lastName: Optional<string>;
};

type PostHogAnalyticsProviderProps = {
  children: ReactNode;
  isEnabled: boolean;
  identity: PostHogAnalyticsIdentity | null;
};

type PostHogUserIdentityProps = {
  identity: PostHogAnalyticsIdentity | null;
};

const PostHogUserIdentity = ({ identity }: PostHogUserIdentityProps) => {
  const posthog = usePostHog();

  useEffect(() => {
    if (identity) {
      posthog.identify(identity.uniqueId, {
        email: identity.email,
        phone_number: toPhone(identity.phoneNumber),
        first_name: identity.firstName,
        last_name: identity.lastName,
      });
    } else {
      posthog.reset();
    }
  }, [identity, posthog]);

  return null;
};

const PostHogAnalyticsProvider = ({
  children,
  identity,
  isEnabled,
}: PostHogAnalyticsProviderProps) => {
  if (!isEnabled || !window.POSTHOG_PUBLIC_API_KEY) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider apiKey={window.POSTHOG_PUBLIC_API_KEY} options={postHogOptions}>
      <PostHogUserIdentity identity={identity} />
      {children}
    </PostHogProvider>
  );
};

export default PostHogAnalyticsProvider;
